<template>
    <div class="ppv2-w-full">
        <div class="ppv2-p-4 md:ppv2-p-8">
            <header class="ppv2-flex ppv2-items-center ppv2-justify-between ppv2-mb-6">
                <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <!-- Dynamic Title -->
                    <h1 class="poppinsSemiBold ppv2-text-2xl md:ppv2-text-3xl">
                        {{ $t(titleKey) }}
                    </h1>
                </div>
            </header>
            <div v-if="type === 'materiel'">
                <form @submit.prevent="saveEquipement" class="ppv2-space-y-6">
                    <!-- Name -->
                    <CustomInput
                        v-model="formMateriel.name"
                        :state="getState('name')"
                        :errorText="currentErrors.name"
                        :label="$t('equipements.form.name.label')"
                        :placeholder="$t('equipements.form.name.placeholder')"
                        field="name"
                        :required="true"
                        @input="validateField('name')"
                    />

                    <!-- Status -->
                    <BaseSelect
                        v-model="formMateriel.status"
                        :error="currentErrors.status"
                        :label="$t('equipements.form.type.label')"
                        :options="option_type"
                        field="status"
                        :required="true"
                        @input="validateField('status')"
                    />

                    <!-- Capacity -->
                    <CustomInput
                        v-model="formMateriel.capacity"
                        :state="getState('capacity')"
                        :errorText="currentErrors.capacity"
                        :label="$t('equipements.form.capacity.label')"
                        :placeholder="$t('equipements.form.capacity.placeholder')"
                        field="capacity"
                        :required="true"
                        @input="validateField('capacity')"
                    />

                    <!-- Diseable (only edit) -->
                    <CustomInput
                        v-if="hasId"
                        v-model="formMateriel.diseable"
                        :state="getState('diseable')"
                        :errorText="currentErrors.diseable"
                        :label="$t('equipements.form.diseable.label')"
                        :placeholder="$t('equipements.form.diseable.placeholder')"
                        field="diseable"
                        @input="validateField('diseable')"
                    />

                    <div class="ppv2-flex ppv2-justify-center ppv2-pt-4" :style="{ gap: normalizeSize(16, true) }">
                        <BaseButton type="button" variant="outline" @click="goBack">
                            {{ $t('common.cancel') }}
                        </BaseButton>
                        <BaseButton type="submit" variant="primary" :loading="isLoading">
                            {{ $t('common.save') }}
                        </BaseButton>
                    </div>
                </form>
            </div>
            <div v-else>
                <form @submit.prevent="saveEquipement" class="ppv2-space-y-6">
                    <!-- Name -->
                    <CustomInput
                        v-model="formSalle.name"
                        :state="getState('name')"
                        :errorText="currentErrors.name"
                        :label="$t('spaces.form.name.label')"
                        :placeholder="$t('spaces.form.name.placeholder')"
                        field="name"
                        :required="true"
                        @input="validateField('name')"
                    />

                    <!-- Capacity -->
                    <CustomInput
                        v-model="formSalle.capacity"
                        type="number"
                        :state="getState('capacity')"
                        :errorText="currentErrors.capacity"
                        :label="$t('spaces.form.capacity.label')"
                        :placeholder="$t('spaces.form.capacity.placeholder')"
                        field="capacity"
                        :required="true"
                        @input="validateField('capacity')"
                    />

                    <!-- Price -->
                    <CustomInput
                        v-model="formSalle.price"
                        type="number"
                        :state="getState('price')"
                        :errorText="currentErrors.price"
                        :label="$t('spaces.form.price.label')"
                        :placeholder="$t('spaces.form.price.placeholder')"
                        field="price"
                        @input="validateField('price')"
                    />

                    <!-- Mixing switch -->
                    <BaseSwitch v-model="formSalle.authorize_mixing" :label="$t('spaces.form.switches.authorize_mixing')" />
                    <!--BaseSwitch v-model="formSalle.link_space" :label="$t('spaces.form.switches.link_space')" /-->

                    <!-- Karafun -->
                    <BaseSwitch v-model="formSalle.karafun" :label="$t('experience.step3.installation.karafun')" />
                    <div v-if="formSalle.karafun" class="ppv2-mt-4">
                        <KarafunDevice :selectedDevices.sync="formSalle.devices" :devices="devices" @sync="fetchKarafunDevices" />
                    </div>

                    <div class="ppv2-flex ppv2-justify-center ppv2-pt-4" :style="{ gap: normalizeSize(16, true) }">
                        <BaseButton type="button" variant="outline" @click="goBack">
                            {{ $t('common.return') }}
                        </BaseButton>
                        <BaseButton type="submit" variant="primary" :loading="isLoading">
                            {{ $t('common.save') }}
                        </BaseButton>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'

import { ArrowLeft } from 'lucide-vue'
import BaseButton from '../../Shared/BaseButton.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import CustomInput from '../../Inputs/CustomInput.vue'
import KarafunDevice from '../Experience/components/KarafunDevice.vue'
import axios from 'axios'

export default {
    name: 'BoEquipementsV3Edit',

    components: {
        ArrowLeft,
        BaseButton,
        CustomInput,
        BaseSelect,
        BaseSwitch,
        KarafunDevice,
    },

    props: {
        id: {
            type: [String, Number],
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        object: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            isLoading: false,

            // Form matériel
            formMateriel: {
                id: this.object.id ?? 0,
                name: this.object.name ?? '',
                capacity: this.object.quantity ?? 0,
                status: this.object.status ?? 0,
                diseable: this.object.diseable ?? 0,
            },

            // Form espace
            formSalle: {
                id: this.object.id ?? 0,
                name: this.object.name ?? '',
                capacity: this.object.capacity ?? 0,
                price: this.object.price ?? 0,
                type: this.object.type ?? 0,
                authorize_mixing: this.object.authorize_mixing ?? 0,
                link_space: false,
                karafun: this.object.karafun_devices?.length > 0,
                devices: this.object.karafun_devices?.map((d) => d.id) || [],
            },
            devices: [],
            loadingDevices: false,

            option_type: [
                { label: this.$t('equipements.form.options.disabled'), value: 0 },
                { label: this.$t('equipements.form.options.free'), value: 1 },
                { label: this.$t('equipements.form.options.repair'), value: 2 },
            ],
            option_space_type: [
                { label: this.$t('spaces.form.types.private'), value: 'private' },
                { label: this.$t('spaces.form.types.public'), value: 'public' },
            ],
            errors: {},
        }
    },
    computed: {
        hasId() {
            return this.id != null && this.id != -1
        },

        // Retourne automatiquement le bon formulaire selon le type
        currentForm() {
            return this.type === 'materiel' ? this.formMateriel : this.formSalle
        },

        currentErrors() {
            return this.errors[this.type] ?? {}
        },
        titleKey() {
            return this.type === 'materiel'
                ? this.hasId
                    ? 'equipements.form.title_edit'
                    : 'equipements.form.title_create'
                : this.hasId
                  ? 'spaces.form.title_edit'
                  : 'spaces.form.title_create'
        },
    },
    methods: {
        normalizeSize,
        goBack() {
            const tab = this.type === 'materiel' ? 'equipements' : 'installations'
            window.location.href = `/back-office/v3/resource?tab=${tab}`
        },

        clearError(field) {
            if (!this.errors[this.type]) return
            delete this.errors[this.type][field]
        },

        getState(field) {
            const err = this.currentErrors[field]
            if (err) return 'error'
            if (this.currentForm[field]) return 'valid'
            return 'default'
        },

        validateField(field) {
            const form = this.currentForm
            const err = {}

            if (this.type === 'materiel') {
                if (field === 'name' && !form.name) err.name = this.$t('experience.errors.required')
                if (field === 'capacity' && !form.capacity) err.capacity = this.$t('experience.errors.required')
                if (field === 'status' && (form.status === null || form.status === ''))
                    err.status = this.$t('experience.errors.required')
            } else {
                if (field === 'name' && !form.name) err.name = this.$t('experience.errors.required')
                if (field === 'capacity' && !form.capacity) err.capacity = this.$t('experience.errors.required')
            }

            this.$set(this.errors, this.type, {
                ...this.errors[this.type],
                ...err,
            })
            console.log(JSON.stringify(this.errors))

            if (!Object.keys(err).length) {
                this.clearError(field)
            }

            return !Object.keys(err).length // return true if valid
        },

        async saveEquipement() {
            // Validate all fields
            const fields = Object.keys(this.currentForm)
            let isValid = true
            fields.forEach((field) => {
                if (!this.validateField(field)) isValid = false
            })
            if (!isValid) return

            this.isLoading = true

            try {
                const payload = {
                    ...this.currentForm,
                    type_equipement: this.type,
                }

                if (this.type === 'space') {
                    // Send karafun_devices if karafun is enabled, otherwise empty array
                    payload.karafun_devices = this.formSalle.karafun ? this.formSalle.devices : []
                }

                await axios.patch('/api/v3/equipements/update', payload)

                this.$toast.success(
                    this.hasId
                        ? this.$t('equipements.notifications.success_update')
                        : this.$t('equipements.notifications.success_add')
                )
                setTimeout(() => this.goBack(), 1000)
            } catch (e) {
                if (e.response?.status === 422) {
                    this.errors = {
                        [this.type]: e.response.data.errors,
                    }
                    return
                }
                this.$toast.error(e.message)
            } finally {
                this.isLoading = false
            }
        },
        async fetchKarafunDevices() {
            this.loadingDevices = true
            try {
                const response = await axios.get('/api/v1/karafun/list')
                this.devices = response.data.devices || response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loadingDevices = false
            }
        },
    },
    mounted() {
        if (this.type === 'space') {
            this.fetchKarafunDevices()
        }
    },
}
</script>
